import { Box, CircularProgress, Divider, MenuItem, Select, SelectChangeEvent, styled, Theme } from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { endpoints } from '../../../constants/routing';
import { DISPLAY_CURRENCY } from '../../../constants/users';
import useFetch from '../../../hooks/useFetch';
import { selectUserDisplayCurrency } from '../../../store/selectors/auth';
import { selectCurrencyCodes } from '../../../store/selectors/shell';
import { IEditUserProfileRequestPayload } from '../../../types/users';
import { handleApiError } from '../../../utils/ui';

const selectStyles = (theme: Theme) => ({
  minHeight: 25,
  height: 25,
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightRegular,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    minHeight: 25,
  },
  '& .MuiSelect-icon': {
    right: 5,
    color: theme.palette.text.secondary,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
});

const SelectWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  minWidth: 120,
});

const LoaderWrapper = styled(Box)({
  marginTop: 5,
  marginLeft: 16,
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: 14,
  height: 36,
  minHeight: 36,
  padding: theme.spacing(1, 2),
  borderLeft: '3px solid',
  borderColor: 'transparent',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  '&.Mui-selected': {
    borderColor: theme.wl?.portal_accent_color,
  },
}));

const StyledDivider = styled(Divider)({
  margin: '0 !important',
});

export const MuiCurrencySelector: React.FC = () => {
  const displayCurrency = useSelector(selectUserDisplayCurrency);
  const currencyCodes = useSelector(selectCurrencyCodes);

  const { make: editUserProfile, isLoading } = useFetch<IEditUserProfileRequestPayload, never>({
    endpoint: endpoints.editUserProfile,
    showToastOnError: true,
    method: 'POST',
    useMonolithApi: true,
  });

  const handleEvent = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  }, []);

  const menuProps = {
    PaperProps: {
      sx: {
        width: 90,
        transform: 'translateX(-60px)',
        borderRadius: 0.5,
      },
      onClick: handleEvent,
      onMouseDown: handleEvent,
      onTouchStart: handleEvent,
    },
  };

  const handleDisplayCurrencyChange = useCallback(
    (event: SelectChangeEvent<string>, child: React.ReactNode) => {
      const value = event.target.value || '';
      editUserProfile({
        body: {
          [DISPLAY_CURRENCY]: value,
        },
      }).catch(handleApiError(`Something bad happened. Display Currency wasn't changed.`));
    },
    [editUserProfile],
  );

  const currencyOptions = useMemo(() => {
    return currencyCodes
      ? currencyCodes.map((currencyCode) => ({
          value: currencyCode,
          label: `${getSymbolFromCurrency(currencyCode)} ${currencyCode}`,
        }))
      : [];
  }, [currencyCodes]);

  return (
    <SelectWrapper>
      {isLoading ? (
        <LoaderWrapper>
          <CircularProgress size="20px" thickness={4} disableShrink sx={{ color: 'primary.main' }} />
        </LoaderWrapper>
      ) : (
        <Select<string>
          value={displayCurrency || ''}
          onChange={handleDisplayCurrencyChange}
          displayEmpty
          MenuProps={menuProps}
          data-action="header_currency_selector"
          sx={selectStyles}
          onClick={handleEvent}
          onMouseDown={handleEvent}
          onTouchStart={handleEvent}
        >
          <StyledMenuItem value="">None</StyledMenuItem>
          <StyledDivider />
          {currencyOptions.map((option) => (
            <StyledMenuItem key={option.value} value={option.value}>
              {option.label}
            </StyledMenuItem>
          ))}
        </Select>
      )}
    </SelectWrapper>
  );
};
